import type { MaybeRefOrGetter, Ref } from 'vue';
import type { AnyItem, CompositePrimaryKey, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { pickByPrimitiveFields, stringifyStableId } from '#layers/data-model/public';

export type SelectionMode = 'single' | 'multiple';

interface UseSelectionOptions {
	selection: Ref<CompositePrimaryKey[]>;
	primaryKeyFields: MaybeRefOrGetter<ResolvedMonospacePrimitiveField[]>;
	selectionKeys?: MaybeRefOrGetter<ResolvedMonospacePrimitiveField[] | undefined>;
	projectionKeys?: MaybeRefOrGetter<ResolvedMonospacePrimitiveField[] | undefined>;
	selectionMode?: MaybeRefOrGetter<SelectionMode | undefined>;
}

export function useSelection(options: UseSelectionOptions) {
	const currentMode = computed(() => toValue(options.selectionMode) ?? 'multiple');
	const selectionKeys = computed(() => toValue(options.selectionKeys) ?? toValue(options.primaryKeyFields));
	const selectionFields = computed(() => [...new Map([
		...selectionKeys.value,
		...toValue(options.projectionKeys) ?? [],
	].map((field) => [field.apiName, field])).values()]);

	function selectionId(item: AnyItem) {
		return stringifyStableId(pickByPrimitiveFields(item, selectionKeys.value));
	}

	const selectedIds = computed(() => new Set(options.selection.value.map((item) => selectionId(item))));

	function projectSelection(items: AnyItem[], current: CompositePrimaryKey[]) {
		if (currentMode.value === 'single') {
			return items[0] ? [pickByPrimitiveFields(items[0], selectionFields.value)] : [];
		}

		const selected = new Map(current.map((item) => [selectionId(item), item]));

		for (const item of items) {
			selected.set(selectionId(item), pickByPrimitiveFields(item, selectionFields.value));
		}

		return [...selected.values()];
	}

	function clear() {
		options.selection.value = [];
	}

	function add(...items: AnyItem[]) {
		if (items.length === 0) return;
		options.selection.value = projectSelection(items, options.selection.value);
	}

	function replace(...items: AnyItem[]) {
		options.selection.value = projectSelection(items, []);
	}

	function remove(...items: AnyItem[]) {
		if (items.length === 0) return;

		const keysToRemove = new Set(items.map((item) => selectionId(item)));

		options.selection.value = options.selection.value.filter((item) => !keysToRemove.has(selectionId(item)));
	}

	function toggleSelection(item: AnyItem) {
		isSelected(item) ? remove(item) : add(item);
	}

	function isSelected(item: AnyItem) {
		return selectedIds.value.has(selectionId(item));
	}

	const isSelectionEmpty = computed(() => options.selection.value.length === 0);

	return {
		selectionFields,
		add,
		replace,
		remove,
		isSelected,
		toggleSelection,
		isSelectionEmpty,
		clear,
	};
}
